import { getTigerResource } from "./tigerResource";

// Tiger seconds count from 1901-01-01; Unix seconds count from 1970-01-01.
export const TIGER_TO_UNIX_OFFSET_SECS = 2_177_452_800;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

// ── Locale date ordering ────────────────────────────────────────────────────
// Mirrors the C++ `RWDate_to_LANG_string`: US renders MM/DD/YYYY, every other
// language renders DD/MM/YYYY. The process language is loaded once at startup
// (`bootstrapTigerLang`) and cached here so the synchronous formatters below can
// consult it during render. Defaults to non-US (DD/MM/YYYY).
let usDateFormat = false;

/** Set whether dates render in US order (MM/DD/YYYY). Called by `bootstrapTigerLang`. */
export function setUsDateFormat(us: boolean): void {
  usDateFormat = us;
}

/** True when dates should render in US order (MM/DD/YYYY). */
export function isUsDateFormat(): boolean {
  return usDateFormat;
}

// ── Process language ────────────────────────────────────────────────────────
// The C++ `enum TIGER_LANG` (`libTiger/tiger_util.h:9`), in declaration order —
// these ordinals go on the wire in `W_DIAG_QUERY_MSG.tiger_lang`, which is what
// the diagnostics buffer switches its reply text on.
const LANG_ORDINALS: Record<string, number> = {
  UK: 0,
  US: 1,
  SP: 2,
  IT: 3,
  FR: 4,
  RU: 5,
  CN: 6,
};

let tigerLangCode = "UK";

/**
 * Cache the process language code (`"UK"`, `"FR"`, …) reported by the backend.
 * Unknown or empty values fall back to `"UK"`, matching the C++
 * `get_TIGER_LANG_config` default. Also refreshes the date ordering, which is
 * derived from the same setting.
 */
export function setTigerLangCode(code: string): void {
  const upper = (code ?? "").trim().toUpperCase();
  tigerLangCode = upper in LANG_ORDINALS ? upper : "UK";
  setUsDateFormat(tigerLangCode === "US");
}

/** The cached process language code (`"UK"`, `"FR"`, …). */
export function getTigerLangCode(): string {
  return tigerLangCode;
}

/** The cached process language as its C++ `TIGER_LANG` ordinal. */
export function getTigerLangOrdinal(): number {
  return LANG_ORDINALS[tigerLangCode] ?? 0;
}

/**
 * Format the day/month/year of a `Date` (read in UTC) in the locale order:
 * US → MM/DD/YYYY, otherwise DD/MM/YYYY. Shared by every date-display helper so
 * the US/UK ordering lives in exactly one place.
 */
function formatDMY(d: Date): string {
  const dd = pad2(d.getUTCDate());
  const mm = pad2(d.getUTCMonth() + 1);
  const yyyy = d.getUTCFullYear();
  return usDateFormat ? `${mm}/${dd}/${yyyy}` : `${dd}/${mm}/${yyyy}`;
}

/**
 * Convert an ISO date (`YYYY-MM-DD`, the value an `<input type="date">` uses) to
 * the locale display order for a text field: US → MM/DD/YYYY, otherwise
 * DD/MM/YYYY. Empty or malformed input yields `""`.
 */
export function isoToLangDisplay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso ?? "").trim());
  if (!m) return "";
  const [, y, mo, d] = m;
  return usDateFormat ? `${mo}/${d}/${y}` : `${d}/${mo}/${y}`;
}

/**
 * Parse a locale-ordered date string (US → MM/DD/YYYY, otherwise DD/MM/YYYY)
 * back to an ISO `YYYY-MM-DD` value for `<input type="date">`/backend state.
 * Returns `""` for empty input and `null` for a malformed or impossible date
 * (e.g. 13/40 or 31 Feb), so callers can reject-and-revert.
 */
export function langDisplayToIso(s: string): string | null {
  const t = (s ?? "").trim();
  if (t === "") return "";
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
  if (!m) return null;
  const a = +m[1], b = +m[2], y = +m[3];
  // Field order follows the locale: US is MM/DD, otherwise DD/MM.
  const mo = usDateFormat ? a : b;
  const d = usDateFormat ? b : a;
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const iso = `${y}-${pad2(mo)}-${pad2(d)}`;
  // Reject inputs that aren't a real calendar date (e.g. 31/02).
  const dt = new Date(`${iso}T00:00:00Z`);
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) {
    return null;
  }
  return iso;
}

/**
 * Format the date of a `Date` (UTC) for graph axis ticks — year always
 * included — in the locale order: US → MM/DD/YYYY, otherwise DD/MM/YYYY.
 */
export function formatGraphDate(d: Date): string {
  const dd = pad2(d.getUTCDate());
  const mm = pad2(d.getUTCMonth() + 1);
  const yyyy = d.getUTCFullYear();
  return usDateFormat ? `${mm}/${dd}/${yyyy}` : `${dd}/${mm}/${yyyy}`;
}

/**
 * Load the process language from the backend and cache whether it is US, so the
 * synchronous date formatters render MM/DD/YYYY for a US site. Works in both the
 * Tauri desktop app (via `invoke`) and the web build (via `/api`). A no-op-safe
 * best-effort call: on any failure the default (DD/MM/YYYY) is kept.
 */
export async function bootstrapTigerLang(): Promise<void> {
  try {
    const isTauri = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
    let code: string | null = null;
    if (isTauri) {
      const { invoke } = await import("@tauri-apps/api/core");
      code = await invoke<string | null>("get_tiger_lang");
    } else if (typeof window !== "undefined") {
      code = await fetch("/api/get_tiger_lang").then((r) => (r.ok ? r.text() : null));
    }
    setTigerLangCode(code ?? "");
  } catch (e) {
    console.error("bootstrapTigerLang failed:", e);
  }
}

/** Convert Tiger seconds to a Unix-epoch `Date` (UTC-based). */
export function tigerSecondsToDate(tigerSecs: number): Date {
  return new Date((tigerSecs - TIGER_TO_UNIX_OFFSET_SECS) * 1000);
}

/**
 * Format Tiger seconds as "HH:MM:SS <date>" in UTC, where the date part is
 * MM/DD/YYYY for a US site and DD/MM/YYYY otherwise (C++ `RWTime_to_LANG_string`).
 */
export function formatTigerDateTime(tigerSecs: number): string {
  const d = tigerSecondsToDate(tigerSecs);
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())} ${formatDMY(d)}`;
}

/**
 * Format Tiger seconds as a date only in UTC: MM/DD/YYYY for a US site,
 * DD/MM/YYYY otherwise (C++ `RWDate_to_LANG_string`).
 */
export function formatTigerDate(tigerSecs: number): string {
  return formatDMY(tigerSecondsToDate(tigerSecs));
}

/**
 * Parse a "HH:MM:SS <date>" UTC string (the format `formatTigerDateTime`
 * produces) back to Tiger seconds. The date part is read in the current locale
 * order — MM/DD/YYYY for a US site, DD/MM/YYYY otherwise — so it is the exact
 * inverse of `formatTigerDateTime` for the legend's editable time cells. Returns
 * null when the string is malformed or the date is not a real calendar date
 * (e.g. 31/02).
 */
export function parseTigerDateTime(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2}):(\d{2})\s+(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s.trim());
  if (!m) return null;
  const hh = +m[1], mi = +m[2], ss = +m[3];
  // Field order follows the locale: US is MM/DD, otherwise DD/MM.
  const dd = usDateFormat ? +m[5] : +m[4];
  const mo = usDateFormat ? +m[4] : +m[5];
  const yyyy = +m[6];
  if (hh > 23 || mi > 59 || ss > 59 || mo < 1 || mo > 12 || dd < 1 || dd > 31) return null;
  const ms = Date.UTC(yyyy, mo - 1, dd, hh, mi, ss);
  const d = new Date(ms);
  // Reject inputs that JS rolled over (e.g. 31/02 → 03/03).
  if (d.getUTCFullYear() !== yyyy || d.getUTCMonth() !== mo - 1 || d.getUTCDate() !== dd) return null;
  return Math.floor(ms / 1000) + TIGER_TO_UNIX_OFFSET_SECS;
}

/**
 * Convert a date ("YYYY-MM-DD", from an <input type="date">) and a time
 * ("HH:MM" or "HH:MM:SS", from an <input type="time">) into Tiger seconds
 * (since 1901-01-01). Interpreted as UTC to match the graph windows. Returns
 * null when either field is empty or malformed.
 */
export function dateTimeToTigerSeconds(date: string, time: string): number | null {
  if (!date || !time) return null;
  const [y, mo, d] = date.split("-").map(Number);
  const [h, mi, s] = time.split(":").map(Number);
  if ([y, mo, d, h, mi].some((n) => !Number.isFinite(n))) return null;
  const unixSecs = Math.floor(Date.UTC(y, mo - 1, d, h, mi, s || 0) / 1000);
  return unixSecs + TIGER_TO_UNIX_OFFSET_SECS;
}

/**
 * Shared range guard for every start/end picker dialog: enforce the rule that the
 * end must be **at least equal to** the start (`end >= start`; equal is allowed).
 *
 * Pass two order-comparable bounds of the SAME kind:
 *  - Tiger-seconds numbers, for date+time pickers (via `dateTimeToTigerSeconds`), or
 *  - zero-padded "YYYY-MM-DD" / "HH:MM:SS" strings, for date-only / time-only
 *    pickers (lexicographic order matches chronological order when zero-padded).
 *
 * Returns the localized error message (`TIGER_ERROR_START_TIME`) when `end` is
 * strictly before `start`. Returns null when the range is valid OR when either
 * bound is missing/unparseable — callers keep their own required-field checks, so
 * this helper never blocks on emptiness, only on an inverted range.
 */
export function validateEndAfterStart(
  start: number | string | null | undefined,
  end: number | string | null | undefined,
): string | null {
  if (start === null || start === undefined || start === "") return null;
  if (end === null || end === undefined || end === "") return null;
  if (typeof start === "number" && !Number.isFinite(start)) return null;
  if (typeof end === "number" && !Number.isFinite(end)) return null;
  return end < start ? getTigerResource("TIGER_ERROR_START_TIME") : null;
}

/**
 * Cap a load/replay window to `tiger_max_replay`: the duration (in seconds)
 * must be **at most** `maxReplaySeconds` (equal allowed, matching the C++
 * `time_diff > get_tiger_max_replay()` check). Returns the localized
 * `TIGER_ERROR_LOAD_TIME` message — with `%d` filled in as the cap in whole
 * hours — when the duration exceeds the cap, else null. Skips (returns null)
 * for a non-finite duration or a non-positive cap, so callers can pass an
 * unloaded/zero max without spuriously blocking.
 */
export function validateMaxDuration(
  durationSeconds: number,
  maxReplaySeconds: number,
): string | null {
  if (!Number.isFinite(durationSeconds)) return null;
  if (!Number.isFinite(maxReplaySeconds) || maxReplaySeconds <= 0) return null;
  if (durationSeconds <= maxReplaySeconds) return null;
  const hours = Math.floor(maxReplaySeconds / 3600);
  return getTigerResource("TIGER_ERROR_LOAD_TIME").replace("%d", String(hours));
}

/** Parse an "HH:MM:SS" (or "HH:MM") interval into seconds. Returns 0 on empty. */
export function intervalToSeconds(hms: string): number {
  if (!hms) return 0;
  const [h, m, s] = hms.split(":").map((n) => Number(n) || 0);
  return h * 3600 + m * 60 + (s || 0);
}

/** Format an elapsed-seconds value (may be negative) as "HH:MM:SS". */
export function formatElapsedHHMMSS(secs: number): string {
  const sign = secs < 0 ? "-" : "";
  const abs = Math.floor(Math.abs(secs));
  return `${sign}${pad2(Math.floor(abs / 3600))}:${pad2(Math.floor((abs % 3600) / 60))}:${pad2(abs % 60)}`;
}

/** Format a positive duration in seconds as "HH:MM:SS". */
export function formatDurationSecs(secs: number): string {
  const s = Math.max(0, Math.floor(secs));
  return `${pad2(Math.floor(s / 3600))}:${pad2(Math.floor((s % 3600) / 60))}:${pad2(s % 60)}`;
}
